import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty } from "./AdminLeads";
import { toast } from "sonner";
import { Trash2, Pencil, Plus } from "lucide-react";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", category: "Insights", read_time: "4 min", published: true };

export function AdminBlogs() {
  const qc = useQueryClient();
  const { data: blogs = [], isLoading } = useQuery({ queryKey: ["admin-blogs"], queryFn: () => api.get("/blogs?all=true").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm(b); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/blogs/${editing}`, form);
      else await api.post("/blogs", form);
      qc.invalidateQueries({ queryKey: ["admin-blogs"] });
      qc.invalidateQueries({ queryKey: ["blogs"] });
      toast.success(editing ? "Article updated" : "Article published");
      setOpen(false);
    } catch (err) { toast.error("Could not save article"); }
  };
  const remove = async (id) => {
    await api.delete(`/blogs/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-blogs"] });
    qc.invalidateQueries({ queryKey: ["blogs"] });
    toast.success("Article deleted");
  };

  return (
    <div data-testid="admin-blogs">
      <div className="mb-6 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="blog-new-btn" className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
              <Plus className="mr-2 h-4 w-4" /> New Article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold uppercase">{editing ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Input required data-testid="blog-title" className="tool-input rounded-none" placeholder="Title" value={form.title} onChange={set("title")} />
              <Input data-testid="blog-excerpt" className="tool-input rounded-none" placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")} />
              <div className="grid grid-cols-2 gap-3">
                <Input className="tool-input rounded-none" placeholder="Category" value={form.category} onChange={set("category")} />
                <Input className="tool-input rounded-none" placeholder="Read time" value={form.read_time} onChange={set("read_time")} />
              </div>
              <Input data-testid="blog-image" className="tool-input rounded-none" placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} />
              <Textarea required data-testid="blog-content" className="tool-input min-h-[220px] rounded-none" placeholder="Content (use blank lines between paragraphs)" value={form.content} onChange={set("content")} />
              <Button type="submit" data-testid="blog-save" className="w-full rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
                {editing ? "Update" : "Publish"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? <p className="font-mono text-sm text-muted-foreground">Loading…</p>
        : !blogs.length ? <Empty label="No articles yet. Write your first one." />
        : (
        <div className="grid gap-px border border-border bg-border">
          {blogs.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-4 bg-background p-5" data-testid={`admin-blog-${b.id}`}>
              <div className="min-w-0">
                <h4 className="truncate font-display text-lg font-bold uppercase text-foreground">{b.title}</h4>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{b.category} · {b.read_time}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(b)} data-testid={`blog-edit-${b.id}`} className="text-muted-foreground hover:text-cyan"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(b.id)} data-testid={`blog-delete-${b.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
